import React from 'react';
import { Divider, SelectChangeEvent } from '@mui/material';
import { CircularProgress } from '@mui/material';
import GuestFormFields from './GuestFormFields';
import type { GuestFormValues, GuestFormErrors } from './GuestValidation';

interface GuestManualEntrySectionProps {
  stage: number;
  formValues: GuestFormValues;
  errors: GuestFormErrors;
  needsDiveGear: boolean;
  checkedItems: { [key: string]: boolean };
  totalAmount: number;
  gearPrices: {
    bcd: number;
    wetsuit: number;
    fins: number;
    mask: number;
    regulator: number;
    diveComputer: number;
    torch: number;
  };
  guestNumber: number;
  isSubmitting: boolean;
  onInputChange: (
    field: string,
  ) => (
    event:
      | React.ChangeEvent<HTMLInputElement | { value: unknown }>
      | SelectChangeEvent<string>,
  ) => void;
  onPhoneChange: (field: string, value: string) => void;
  onDateChange: (field: string, value: string) => void;
  onCheckboxChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onGearSelection: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderSizeChips: (
    item: 'bcd' | 'wetsuit' | 'fins',
    sizes: string[],
  ) => React.ReactNode;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
}

const GuestManualEntrySection: React.FC<GuestManualEntrySectionProps> = ({
  stage,
  formValues,
  errors,
  needsDiveGear,
  checkedItems,
  totalAmount,
  gearPrices,
  guestNumber,
  isSubmitting,
  onInputChange,
  onPhoneChange,
  onDateChange,
  onCheckboxChange,
  onGearSelection,
  renderSizeChips,
  onBack,
  onNext,
  onSubmit,
}) => {
  const getStageTitle = () => {
    switch (stage) {
      case 1:
        return 'Basic personal information';
      case 2:
        return 'Travel documentation';
      case 3:
        return 'Dive Certification';
      case 4:
        return 'Airport transfer';
      case 5:
        return 'Rental dive equipment';
      default:
        return '';
    }
  };

  const getStageDescription = () => {
    switch (stage) {
      case 1:
        return 'Provide basic personal details to inform the boat organizer';
      case 2:
        return 'Provide your passport information to book your national park entrance';
      case 3:
        return 'Provide your dive certification details';
      case 4:
        return 'Provide your airport transfer details';
      case 5:
        return 'Select the rental dive equipment you need';
      default:
        return '';
    }
  };

  const showBackButton = !(guestNumber === 1 && stage === 1);

  return (
    <div className="flex flex-col px-6 pb-6 pt-5">
      <div className="flex items-start gap-4 pr-10">
        <div className="relative inline-flex shrink-0">
          <CircularProgress
            variant="determinate"
            value={(stage / 5) * 100}
            size={56}
            thickness={4}
            sx={{ color: '#005DAB' }}
            aria-label={`Step ${stage} of 5`}
          />
          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-neutral-800">
            {stage}/5
          </span>
        </div>
        <div className="flex min-w-0 flex-col">
          <h2 className="text-xl font-semibold leading-tight text-neutral-800 sm:text-2xl">
            {getStageTitle()}
          </h2>
          <p className="mt-1.5 text-sm text-neutral-500 sm:text-base">
            {getStageDescription()}
          </p>
        </div>
      </div>

      <GuestFormFields
        stage={stage}
        formValues={formValues}
        errors={errors}
        needsDiveGear={needsDiveGear}
        checkedItems={checkedItems}
        totalAmount={totalAmount}
        gearPrices={gearPrices}
        onInputChange={onInputChange}
        onPhoneChange={onPhoneChange}
        onDateChange={onDateChange}
        onCheckboxChange={onCheckboxChange}
        onGearSelection={onGearSelection}
        renderSizeChips={renderSizeChips}
      />

      <Divider sx={{ marginTop: '24px', marginBottom: '20px' }} />

      <div
        className={`flex flex-col-reverse gap-3 sm:flex-row sm:items-center ${
          showBackButton ? 'sm:justify-between' : 'sm:justify-end'
        }`}
      >
        {showBackButton && (
          <button
            type="button"
            className="h-11 w-full rounded-lg border border-neutral-800 bg-neutral-00 px-5 font-semibold text-neutral-800 transition-colors hover:bg-neutral-20 sm:w-auto"
            onClick={onBack}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="h-11 w-full rounded-lg bg-neutral-800 px-6 font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[120px] sm:w-auto"
          disabled={isSubmitting}
          onClick={stage === 5 ? onSubmit : onNext}
        >
          {stage === 5 ? (isSubmitting ? 'Submitting...' : 'Finish') : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default GuestManualEntrySection;
